import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  getBrandLogoLinkUrl,
  getMainNavigation,
  getShowMinimalNavigation,
  getShowMinimalNavigationHelp,
  getShowNavigation,
} from '@core/jss-routing';
import { NavigationEnergiedirectModule } from '@core/navigation-legacy';
import {
  getEcmpHeader,
  getEcmpLogo,
  helpClickedAction,
} from '@core/navigation-store';
import { getLogoHomepageNavigationLabel } from '@core/navigation-ui';
import { NavigationDesktopComponent } from '@core/navigation-ui/navigation-desktop';
import { NavigationEcmpLogoComponent } from '@core/navigation-ui/navigation-ecmp-logo';
import { NavigationHeaderComponent } from '@core/navigation-ui/navigation-header';
import { NavigationHelpNeededButtonComponent } from '@core/navigation-ui/navigation-help-needed-button';
import { NavigationLogoComponent } from '@core/navigation-ui/navigation-logo';
import { NavigationMobileComponent } from '@core/navigation-ui/navigation-mobile';
import { NavigationServiceButtonComponent } from '@core/navigation-ui/navigation-service-button';
import { SkipLinkComponent } from '@core/navigation-ui/skip-link';
import { loginUrl } from '@core/settings';
import { GenericModalSources } from '@legacy/common-ui-shared-interfaces';
import { Store } from '@ngrx/store';
import { NgTemplateOutlet } from '@angular/common';
import { DeployUrlPipe } from '@innogy/utils/environment';

@Component({
  selector: 'wl-header-open-ed-container',
  standalone: true,
  imports: [
    NavigationHeaderComponent,
    NavigationEnergiedirectModule,
    SkipLinkComponent,
    NavigationDesktopComponent,
    NavigationLogoComponent,
    NavigationServiceButtonComponent,
    NavigationMobileComponent,
    NavigationEcmpLogoComponent,
    NavigationHelpNeededButtonComponent,
    NgTemplateOutlet,
  ],
  providers: [DeployUrlPipe],
  templateUrl: './header-open-ed-container.component.html',
})
export class HeaderOpenEdContainerComponent {
  readonly #store = inject(Store);
  readonly #config = inject(ENVIRONMENT_CONFIG);

  logoUrl = this.#store.selectSignal(getBrandLogoLinkUrl);
  readonly logoLabel = signal(getLogoHomepageNavigationLabel(this.#config));

  // TechDebt: ecmpLogoFromQueryParam will be decommissioned in favor of ecmpLogoFromPage in https://jira.essent.nl/jira/browse/CTR-1855
  readonly ecmpLogoFromChannelRecognitionConfig =
    this.#store.selectSignal(getEcmpHeader);
  readonly ecmpLogoFromSitecoreContent = this.#store.selectSignal(getEcmpLogo);
  readonly ecmpLogoSource = computed(
    () =>
      this.ecmpLogoFromSitecoreContent()?.logo ||
      this.deployUrlPipe.transform(
        this.ecmpLogoFromChannelRecognitionConfig().logo ?? ''
      )
  );
  readonly ecmpLogo = computed(
    () =>
      this.ecmpLogoFromSitecoreContent() ||
      this.ecmpLogoFromChannelRecognitionConfig()
  );

  loginLinkField = this.#store.selectSignal(loginUrl);
  readonly loginUrl = computed(() => this.loginLinkField()?.value.href);
  readonly loginText = computed(() => this.loginLinkField()?.label);
  readonly mainNavigation = toSignal(this.#store.pipe(getMainNavigation));
  navigationActive = this.#store.selectSignal(getShowNavigation);
  minimalNavigationActive = this.#store.selectSignal(getShowMinimalNavigation);
  readonly sitecoreDataLoaded = computed(
    () => !!this.mainNavigation()?.isNavigationAvailable
  );

  readonly showNavigation = computed(
    () => this.navigationActive() && this.sitecoreDataLoaded()
  );
  showMinimalNavigationHelp = this.#store.selectSignal(
    getShowMinimalNavigationHelp
  );
  readonly showEcmpLogo = computed(
    () => this.minimalNavigationActive() && this.ecmpLogo()?.logo
  );
  readonly showMainNavigation = computed(
    () =>
      !this.minimalNavigationActive() &&
      this.mainNavigation()?.isNavigationAvailable
  );
  readonly showLoginButton = computed(
    () => this.loginUrl() && this.loginText()
  );

  enableA11yHeader = this.#config.a11y.enableA11YHeader;

  constructor(private readonly deployUrlPipe: DeployUrlPipe) {}

  dispatchHelpAction() {
    this.#store.dispatch(
      helpClickedAction({
        payload: {
          source: GenericModalSources.CHATBOT,
        },
      })
    );
  }
}
